import React from 'react'
import { useAppSelector } from '../../../hooks/redux'

import BaseConversation from '../BaseConversation'

import { findFilterID, getLatestEvent } from '../../../utilities'
import { useUserAPI } from '../../../providers/UserAPIProvider'
import { useTyping } from '../../../providers/TypingProvider'
import { selectDMs } from '../../../slices/dms'
import { useEnvironment } from '../../../providers/EnvironmentProvider'
import { usePresence } from '../../../hooks/usePresence'
import { useAgent } from '../../../providers/AgentProvider'

const PeerConversation = ({
  conversation,
  selected,
  onClick,
  sorting,
  handleCloseConversation,
  latestActivity = []
}) => {
  // Gets props from Conversations
  const { currentUser } = useAgent()
  const { loadUser } = useUserAPI()
  const { getTyping } = useTyping()
  const { dms } = useAppSelector(selectDMs)
  const { user } = useEnvironment()
  const { queryPresence } = usePresence()

  const {
    name,
    owner_id,
    isNew,
    users = [],
    session,
    messages = [],
    notes = [],
    isGroup
  } = conversation

  const isYours = true

  const isPersonalSpace = React.useMemo(
    () => users?.every(i => currentUser()?.id === i) && !isGroup,
    [currentUser, users, isGroup]
  )

  // Gets list of typing people
  const typingNames = React.useMemo(
    () => getTyping(session),
    [getTyping, session]
  )

  const userIDs = React.useMemo(() => {
    if (users.length === 1) {
      return users
    } else {
      return users.filter(id => id !== user?.id)
    }
  }, [users, user])

  const userIDsWithOwner = React.useMemo(() => {
    if (conversation.users.includes(owner_id)) {
      const withOwner = [...userIDs, owner_id].filter(Boolean)
      return [...new Set(withOwner)]
    } else {
      // list of users without client and self
      return conversation.users.filter(id => id !== user?.id)
    }
  }, [userIDs, owner_id, conversation.users, user])

  // Check last event, can be message | note | null
  const lastMessage = React.useMemo(() => {
    const isGroup = Boolean(dms[session]?.isGroup)
    const filter = findFilterID(owner_id, isGroup, session)

    const latest = getLatestEvent(
      {
        ...conversation,
        latestActivity
      },
      filter
    )
    return latest
  }, [owner_id, session, conversation, latestActivity, dms])

  // Load all users of this session if needed
  React.useEffect(() => {
    users.forEach(id => loadUser(id))
  }, [users, loadUser])

  // Load user if needed
  React.useEffect(() => {
    if (owner_id) {
      loadUser(owner_id)
    }
  }, [loadUser, owner_id])

  // Updates presence
  React.useEffect(() => {
    if (selected && user) {
      const others = users.filter(id => id !== user.id)
      queryPresence(others)
    }
  }, [selected, user, users, queryPresence])

  const avatarProps = React.useMemo(
    () =>
      isPersonalSpace
        ? { userIDs: [conversation?.user?.id] }
        : { userIDs: userIDsWithOwner },
    [userIDsWithOwner, isPersonalSpace, conversation?.user]
  )

  const handleClose = React.useCallback(
    (clear = false) => {
      handleCloseConversation(session, clear)
    },
    [handleCloseConversation, session]
  )

  const joinedMessages = React.useMemo(
    () =>
      [
        ...messages,
        ...notes,
        ...(Array.isArray(latestActivity) ? latestActivity : [latestActivity])
      ].sort((a, b) => +new Date(b) - +new Date(a)),
    [messages, notes, latestActivity]
  )

  const contentProps = React.useMemo(
    () => ({
      name,
      selected,
      lastMessage,
      isNew,
      typingNames,
      numUsers: isPersonalSpace ? 1 : conversation.users?.length ?? 0,
      handleCloseConversation: handleClose,
      users,
      session,
      isYours,
      messages: joinedMessages,
      conversation,
      isPersonalSpace,
      isPeer: true
    }),
    [
      name,
      selected,
      lastMessage,
      isNew,
      typingNames,
      handleClose,
      users,
      session,
      isYours,
      joinedMessages,
      conversation,
      isPersonalSpace
    ]
  )

  return (
    <BaseConversation
      avatarProps={avatarProps}
      contentProps={contentProps}
      onClick={onClick}
      isGroup={isGroup}
      sorting={sorting}
      scrollID={session}
      isPersonalSpace={isPersonalSpace}
    />
  )
}

export default PeerConversation
